import React, { useRef, useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Camera, X, Check } from 'react-feather';

const PhotoCapture: React.FC = () => {
  const navigate = useNavigate();
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [step, setStep] = useState<1 | 2>(1);
  const [firstPhotoBlob, setFirstPhotoBlob] = useState<Blob | null>(null);
  const [firstPhotoLocation, setFirstPhotoLocation] = useState<{ latitude: number; longitude: number } | null>(null);
  
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  
  // 위치 추적을 위한 Ref
  const watchIdRef = useRef<number | null>(null);
  const locationRef = useRef<{ latitude: number; longitude: number } | null>(null);
  const bestAccuracyRef = useRef<number>(Infinity); // 가장 좋은(낮은) 오차값 저장

  const stopCamera = useCallback(() => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    // 위치 추적 중단
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    
    setIsCameraActive(false);
    setStep(1); 
    setFirstPhotoBlob(null); 
    locationRef.current = null;
    bestAccuracyRef.current = Infinity; // 정확도 초기화
  }, [stream]);

  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
    };
  }, [stream]);

  useEffect(() => {
    if (isCameraActive && stream && videoRef.current) {
      videoRef.current.srcObject = stream;
    }
  }, [isCameraActive, stream]);

  const startCamera = async () => {
    // 1. 위치 추적 먼저 시작
    if (navigator.geolocation) {
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }

      bestAccuracyRef.current = Infinity; // 시작 시 초기화
      console.log("📍 위치 추적 시작 (Best Accuracy 모드)...");
      
      watchIdRef.current = navigator.geolocation.watchPosition(
        (position) => {
          const { latitude, longitude, accuracy } = position.coords;
          
          // [안전장치] 더 정확한 좌표(오차가 더 적은 좌표)가 들어온 경우에만 업데이트
          if (accuracy < bestAccuracyRef.current) {
            console.log(`✅ 더 정확한 위치 수신: 오차 ${bestAccuracyRef.current}m -> ${accuracy}m`);
            bestAccuracyRef.current = accuracy;
            locationRef.current = { latitude, longitude };
          } else {
            console.log(`ℹ️ 덜 정확한 위치 무시: 현재 ${bestAccuracyRef.current}m < 수신 ${accuracy}m`);
          }
        },
        (error) => {
          console.warn("위치 추적 실패:", error);
        },
        {
          enableHighAccuracy: true,
          maximumAge: 0,
          timeout: 10000
        }
      );
    }

    try {
      // 2. 카메라 시작 (사용자 허용 대기 등으로 시간 소요됨)
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
        audio: false
      });
      setStream(mediaStream);
      setIsCameraActive(true);
      setStep(1);

    } catch (err) {
      console.warn('Environment camera not found, trying fallback...', err);
      try {
        const mediaStream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: false
        });
        setStream(mediaStream);
        setIsCameraActive(true);
        setStep(1);
        
        // Fallback 상황에서도 위치 추적은 위에서 이미 시작되었으므로 추가 로직 불필요

      } catch (fallbackErr) {
        console.error('Error accessing camera:', fallbackErr);
        alert('카메라에 접근할 수 없습니다. 권한을 확인해주세요.');
        cameraInputRef.current?.click();
      }
    }
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');

      if (context) {
        canvas.width = video.videoHeight;
        canvas.height = video.videoWidth;
        
        context.save();
        context.translate(0, canvas.height);                                                                          
        context.rotate(-Math.PI / 2);
        context.drawImage(video, 0, 0, video.videoWidth, video.videoHeight);
        context.restore();
        
        canvas.toBlob((blob) => {
          if (!blob) return;

          if (step === 1) {
            // [중요] 첫 번째 사진 찍는 순간 위치 확정 (Lock)
            setFirstPhotoBlob(blob);
            setStep(2);
            
            if (locationRef.current) {
              console.log("🔒 촬영 순간 좌표 고정:", locationRef.current);
              setFirstPhotoLocation(locationRef.current);
              
              // 좌표를 확보했으므로 배터리 절약 및 좌표 튐 방지를 위해 추적 중단
              if (watchIdRef.current !== null) {
                navigator.geolocation.clearWatch(watchIdRef.current);
                watchIdRef.current = null;
              }
            } else {
              console.warn("⚠️ 촬영 순간 확보된 좌표가 없습니다. (GPS 수신 지연)");
              // 만약 아직도 위치가 없다면, 비상용으로 getCurrentPosition 시도
              navigator.geolocation.getCurrentPosition(
                (position) => {
                  console.log("🔄 비상 위치 획득 성공:", position.coords);
                  setFirstPhotoLocation({
                    latitude: position.coords.latitude,
                    longitude: position.coords.longitude
                  });
                },
                (err) => console.error("비상 위치 획득 실패:", err),
                { enableHighAccuracy: true }
              );
            }

          } else {
            mergeAndNavigate(blob);
          }
        }, 'image/jpeg', 0.85);
      }
    }
  };

  const mergeAndNavigate = async (secondBlob: Blob) => {
    if (!firstPhotoBlob) return;

    try {
      const img1 = await createImageBitmap(firstPhotoBlob);
      const img2 = await createImageBitmap(secondBlob);

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (!ctx) return;

      canvas.width = Math.max(img1.width, img2.width);
      canvas.height = img1.height + img2.height;

      ctx.drawImage(img1, 0, 0);
      ctx.drawImage(img2, 0, img1.height);

      canvas.toBlob((mergedBlob) => {
        if (mergedBlob) {
          const file = new File([mergedBlob], 'merged_report_photo.jpg', { type: 'image/jpeg' });
          stopCamera();
          navigate('/report/confirm-photo', { 
            state: { 
              file, 
              previewUrl: URL.createObjectURL(file),
              latitude: firstPhotoLocation?.latitude,
              longitude: firstPhotoLocation?.longitude
            } 
          });
        }
      }, 'image/jpeg', 0.85);

    } catch (error) {
      console.error("Error merging images:", error);
      alert("사진 처리 중 오류가 발생했습니다. 다시 시도해주세요.");
      setStep(1);
      setFirstPhotoBlob(null);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      navigate('/report/confirm-photo', { 
        state: { 
          file, 
          previewUrl: URL.createObjectURL(file) 
        } 
      });
    }
    event.target.value = '';
  };

  if (isCameraActive) {
    return (
      <div className="camera-container">
        <video 
          ref={videoRef} 
          autoPlay 
          playsInline 
          muted
        />
        <canvas ref={canvasRef} style={{ display: 'none' }} />
        
        <div className="camera-overlay">
          <div className={`guide-box step-${step}`}>
            {step === 1 && (
              <>
                <div className="top-boundary-line-1"></div>
                <div className="top-boundary-line-2"></div>
              </>
            )}
            {step === 2 && (
              <div className="right-boundary-line"></div>
            )}

            <div className="guide-text">
              <span className="step-indicator">단계 {step} / 2</span>
              {step === 1 
                ? "빗물받이가 잘 보이도록\n위에서 정면으로 촬영해주세요."
                : "점선을 도로/건물 경계에 맞춰\n빗물받이를 멀리서 촬영해주세요."}
            </div>
          </div>
          
          <div className="camera-controls">
            <button onClick={stopCamera} className="control-button">
              <X size={28} />
              <span>닫기</span>
            </button>
            
            <button onClick={capturePhoto} className="control-button capture-trigger">
            </button>
            
            <div style={{ width: 60 }} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="content-container">
      <div className="photo-capture center-content-text card-container">
        <h2>사진 촬영</h2>
        <p className="secondary-text">
          정확한 신고를 위해<br/>
          <strong>2장의 사진</strong>을 촬영합니다.
        </p>
        
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />

        <div className="capture-options">
          <button onClick={startCamera} className="capture-button">
            <Camera size={24} />
            <span>카메라 켜기</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default PhotoCapture;
